import { Consideration } from './Consideration';
import {
  ParsedSupplement,
  parseSupplementItem,
} from '../utils/supplementItem';
import { ABOUT_MARKER_MAP } from './about-marker-map';
import { normalizeSupplementName } from './holobiomeKits';

export class FurtherConsiderations {
  considerations: Consideration[] = [];

  static createFromConsiderations(considerations: Consideration[]) {
    const furtherConsiderations = new FurtherConsiderations();

    if (considerations) {
      furtherConsiderations.considerations = considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      furtherConsiderations.considerations = [];
    }

    return furtherConsiderations;
  }

  constructor(furtherConsiderations?: FurtherConsiderations) {
    if (!furtherConsiderations) {
      return;
    }

    Object.keys(furtherConsiderations).forEach(key => {
      this[key] = furtherConsiderations[key];
    });

    if (furtherConsiderations.considerations) {
      this.considerations = furtherConsiderations.considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      this.considerations = [];
    }
  }

  findConsiderations(
    categoryId: number,
    section?: string,
    tab?: string,
    brands?: string[],
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      const isBrandMatch = !brands || brands.includes(consideration.brand);

      if (section && tab) {
        return (
          consideration.categoryId === categoryId &&
          consideration.section === section &&
          consideration.tab === tab &&
          isBrandMatch
        );
      }

      return consideration.categoryId === categoryId && isBrandMatch;
    });
  }

  findConsiderationsByCategoryAndSubCategory(
    categoryId: number,
    subCategoryId: number,
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      return (
        consideration.categoryId === categoryId &&
        consideration.subCategoryId === subCategoryId
      );
    });
  }

  // Resolve a marker's "About" description for a given section, keyed off the
  // STABLE categoryId via ABOUT_MARKER_MAP (generated by
  // scripts/buildAboutMarkerMap.js). Because nothing here matches on the sheet
  // `title`, reformatting a marker title in the sheet never breaks the lookup.
  //   1) the marker's own row in this section, else
  //   2) a safe cross-section fallback: if the marker maps to exactly ONE
  //      categoryId table-wide (e.g. a Glucose-borrowed marker described only in
  //      its home section), use it. Markers described in several sections
  //      (ferritin, HDL, cortisol) map to >1 categoryId, so they never fall back
  //      to a wrong-context write-up.
  findAboutConsideration(
    section: string,
    fieldId: string,
  ): Consideration | undefined {
    const sectionMap = ABOUT_MARKER_MAP[section];
    let categoryId = sectionMap ? sectionMap[fieldId] : undefined;

    if (categoryId === undefined) {
      const categoryIds: number[] = [];
      for (const sec of Object.keys(ABOUT_MARKER_MAP)) {
        const cid = ABOUT_MARKER_MAP[sec][fieldId];
        if (cid !== undefined && categoryIds.indexOf(cid) === -1) {
          categoryIds.push(cid);
        }
      }
      if (categoryIds.length === 1) categoryId = categoryIds[0];
    }

    if (categoryId === undefined) return undefined;
    return (this.considerations || []).find(
      c => c.tab === 'About' && c.categoryId === categoryId,
    );
  }

  // Resolve a Reference Library slug (e.g. "ada_diagnosis_classification_diabetes_2026")
  // to its Consideration row. Tags are unique across the table — the sheet's
  // Reference Library tab enforces it — so the first match wins.
  findByTag(tag: string): Consideration | undefined {
    if (!tag) return undefined;
    return (this.considerations || []).find(c => c.tag === tag);
  }

  // Resolve a Supplements bullet to its display parts. A bullet is either a
  // supplement-library tag (a cat 500xxx row — the de-duplicated form, items =
  // [description, link]) or a legacy inline "Name — Description — url" string.
  // findByTag is tried FIRST: supplement tags can contain spaces/hyphens (e.g.
  // "ga - adrenal", "cvo-r") that parseSupplementItem would mis-split on its
  // " - " separator, and a legacy string never matches a tag. Returns null for
  // unstructured lines (e.g. the "Use code …" promo) so callers render them as
  // plain text. Mirrors the Reference Library's findByTag resolution.
  resolveSupplement(item: string): ParsedSupplement | null {
    const tag = (item || '').trim();
    const row = this.findByTag(tag);
    if (row) {
      const [description = '', link = ''] = row.items || [];
      return { name: row.title, description, link: link || undefined };
    }
    return parseSupplementItem(item);
  }

  // Resolve a supplement's product URL by its display NAME (title), from the
  // cat 500xxx supplement catalog. Unlike resolveSupplement (keyed by tag on a
  // recommendation bullet), this looks a product up by name so the Holobiome
  // Roadmap panel — which lists every product in a kit, including grayed-out
  // ones no pattern recommended — can still link to the real Systemic Formulas
  // page. Catalog rows carry NO `tab` and store [description, link] in items;
  // matching the sheet's own URL avoids slug-guessing (e.g. "Terra µBiomic"
  // encodes as terra-%c2%b5biomic, not terra-ubiomic). Returns undefined when
  // the product isn't in the catalog yet, so callers can fall back.
  supplementLinkByName(name: string): string | undefined {
    const target = normalizeSupplementName(name);
    if (!target) return undefined;
    for (const c of this.considerations || []) {
      if (c.tab) continue; // catalog rows are tag-only, never rendered on a tab
      if (c.section !== 'Supplements') continue;
      if (!c.title || normalizeSupplementName(c.title) !== target) continue;
      const link = (c.items || [])[1];
      if (link && /^https?:\/\//.test(link)) return link;
    }
    return undefined;
  }
}